import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './UserManagement.css';

function UserManagement() {
  const { user: currentUser, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [gmailWhitelist, setGmailWhitelist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingWhitelistEntry, setEditingWhitelistEntry] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'user'
  });
  const [whitelistFormData, setWhitelistFormData] = useState({
    email: '',
    role: 'user'
  });

  useEffect(() => {
    if (isAdmin) {
      if (activeTab === 'users') {
        fetchUsers();
      } else if (activeTab === 'gmail-whitelist') {
        fetchGmailWhitelist();
      }
    }
  }, [isAdmin, activeTab]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchGmailWhitelist = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/gmail-whitelist');
      setGmailWhitelist(response.data);
    } catch (error) {
      console.error('Error fetching Gmail whitelist:', error);
      setError('Failed to fetch Gmail whitelist');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      setError('');
      await api.post('/api/users', formData);
      setShowCreateForm(false);
      setFormData({ email: '', password: '', role: 'user' });
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      setError(error.response?.data?.error || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      setError('');
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password; // Don't update password if empty
      }
      await api.put(`/api/users/${editingUser.id}`, updateData);
      setEditingUser(null);
      setFormData({ email: '', password: '', role: 'user' });
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      setError(error.response?.data?.error || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        setError('');
        await api.delete(`/api/users/${userId}`);
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        setError(error.response?.data?.error || 'Failed to delete user');
      }
    }
  };

  const handleCreateWhitelistEntry = async (e) => {
    e.preventDefault();
    try {
      setError('');
      await api.post('/api/admin/gmail-whitelist', whitelistFormData);
      setShowCreateForm(false);
      setWhitelistFormData({ email: '', role: 'user' });
      fetchGmailWhitelist();
    } catch (error) {
      console.error('Error creating whitelist entry:', error);
      setError(error.response?.data?.error || 'Failed to add email to whitelist');
    }
  };

  const handleUpdateWhitelistEntry = async (e) => {
    e.preventDefault();
    try {
      setError('');
      await api.put(`/api/admin/gmail-whitelist/${editingWhitelistEntry.id}`, { 
        role: whitelistFormData.role 
      });
      setEditingWhitelistEntry(null);
      setWhitelistFormData({ email: '', role: 'user' });
      fetchGmailWhitelist();
    } catch (error) {
      console.error('Error updating whitelist entry:', error);
      setError(error.response?.data?.error || 'Failed to update whitelist entry');
    }
  };

  const handleDeleteWhitelistEntry = async (entryId) => {
    if (window.confirm('Are you sure you want to remove this email from the whitelist?')) {
      try {
        setError('');
        await api.delete(`/api/admin/gmail-whitelist/${entryId}`);
        fetchGmailWhitelist();
      } catch (error) {
        console.error('Error deleting whitelist entry:', error);
        setError(error.response?.data?.error || 'Failed to remove email from whitelist');
      }
    }
  };

  const startEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.username, // backend stores email in username field
      password: '',
      role: user.role
    });
    setShowCreateForm(false);
  };

  const cancelEdit = () => {
    setEditingUser(null);
    setEditingWhitelistEntry(null);
    setShowCreateForm(false);
    setFormData({ email: '', password: '', role: 'user' });
    setWhitelistFormData({ email: '', role: 'user' });
  };

  const startEditWhitelistEntry = (entry) => {
    setEditingWhitelistEntry(entry);
    setWhitelistFormData({
      email: entry.email,
      role: entry.role
    });
    setShowCreateForm(false);
    setEditingUser(null);
  };

  const canCreateRole = (role) => {
    if (currentUser.role === 'owner') return true;
    if (currentUser.role === 'admin' && role !== 'owner') return true;
    return false;
  };

  const canManageUser = (user) => {
    if (currentUser.role === 'owner') return true;
    if (currentUser.role === 'admin' && user.role !== 'owner') return true;
    return false;
  };

  if (!isAdmin) {
    return (
      <div className="access-denied">
        <h2>Access Denied</h2>
        <p>You don't have permission to manage users.</p>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="user-management-header">
        <h2>User Management</h2>
        
        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            Users
          </button>
          <button 
            className={`tab ${activeTab === 'gmail-whitelist' ? 'active' : ''}`}
            onClick={() => setActiveTab('gmail-whitelist')}
          >
            Gmail Whitelist
          </button>
        </div>

        <button
          className="create-user-button"
          onClick={() => {
            setShowCreateForm(true);
            setEditingUser(null);
            setEditingWhitelistEntry(null);
          }}
        >
          {activeTab === 'users' ? 'Create User' : 'Add Gmail Account'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      {(showCreateForm || editingUser) && activeTab === 'users' && (
        <form 
          className="user-form" 
          onSubmit={editingUser ? handleUpdateUser : handleCreateUser}
        >
          <h3>{editingUser ? 'Edit User' : 'Create New User'}</h3>
          
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              placeholder="user@example.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Password {editingUser && '(leave empty to keep current)'}</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required={!editingUser}
            />
          </div>

          <div className="form-group">
            <label>Role</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              required
            >
              {canCreateRole('user') && <option value="user">User</option>}
              {canCreateRole('admin') && <option value="admin">Admin</option>}
              {canCreateRole('owner') && <option value="owner">Owner</option>}
            </select>
          </div>

          <div className="form-actions">
            <button type="submit">
              {editingUser ? 'Update User' : 'Create User'}
            </button>
            <button type="button" onClick={cancelEdit}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {(showCreateForm || editingWhitelistEntry) && activeTab === 'gmail-whitelist' && (
        <form 
          className="user-form" 
          onSubmit={editingWhitelistEntry ? handleUpdateWhitelistEntry : handleCreateWhitelistEntry}
        >
          <h3>{editingWhitelistEntry ? 'Edit Gmail Whitelist Entry' : 'Add Gmail Account to Whitelist'}</h3>
          
          <div className="form-group">
            <label>Gmail Address</label>
            <input
              type="email"
              placeholder="user@gmail.com"
              value={whitelistFormData.email}
              onChange={(e) => setWhitelistFormData({ ...whitelistFormData, email: e.target.value })}
              required
              disabled={editingWhitelistEntry} // Disable editing email for existing entries
            />
          </div>

          <div className="form-group">
            <label>Role</label>
            <select
              value={whitelistFormData.role}
              onChange={(e) => setWhitelistFormData({ ...whitelistFormData, role: e.target.value })}
              required
            >
              {canCreateRole('user') && <option value="user">User</option>}
              {canCreateRole('admin') && <option value="admin">Admin</option>}
              {canCreateRole('owner') && <option value="owner">Owner</option>}
            </select>
          </div>

          <div className="form-actions">
            <button type="submit">
              {editingWhitelistEntry ? 'Update Entry' : 'Add to Whitelist'}
            </button>
            <button type="button" onClick={cancelEdit}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="users-table">
        {loading ? (
          <div className="loading">{activeTab === 'users' ? 'Loading users...' : 'Loading whitelist...'}</div>
        ) : activeTab === 'users' ? (
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users
                .filter(user => {
                  // Hide owners from non-owner users
                  if (currentUser.role !== 'owner' && user.role === 'owner') {
                    return false;
                  }
                  return true;
                })
                .map(user => (
                <tr key={user.id}>
                  <td>{user.username}</td>
                  <td>
                    <span className={`role-badge ${user.role}`}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    {user.createdAt ? 
                      new Date(user.createdAt._seconds * 1000).toLocaleDateString() 
                      : 'N/A'
                    }
                  </td>
                  <td>
                    {canManageUser(user) && user.id !== currentUser.id && (
                      <>
                        <button
                          className="edit-button"
                          onClick={() => startEdit(user)}
                        >
                          Edit
                        </button>
                        <button
                          className="delete-button"
                          onClick={() => handleDeleteUser(user.id)}
                        >
                          Delete
                        </button>
                      </>
                    )}
                    {user.id === currentUser.id && (
                      <span className="current-user">You</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Gmail Address</th>
                <th>Role</th>
                <th>Added</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {gmailWhitelist
                .filter(entry => {
                  // Hide owner entries from non-owner users
                  if (currentUser.role !== 'owner' && entry.role === 'owner') {
                    return false;
                  }
                  return true;
                })
                .map(entry => (
                <tr key={entry.id}>
                  <td>{entry.email}</td>
                  <td>
                    <span className={`role-badge ${entry.role}`}>
                      {entry.role}
                    </span>
                  </td>
                  <td>
                    {entry.createdAt ? 
                      new Date(entry.createdAt._seconds * 1000).toLocaleDateString() 
                      : 'N/A'
                    }
                  </td>
                  <td>
                    {canCreateRole(entry.role) && (
                      <>
                        <button
                          className="edit-button"
                          onClick={() => startEditWhitelistEntry(entry)}
                        >
                          Edit
                        </button>
                        <button
                          className="delete-button"
                          onClick={() => handleDeleteWhitelistEntry(entry.id)}
                        >
                          Remove
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default UserManagement;